/**
 * Dining Out — Chef Card.
 *
 * Generates a printable allergy chef card from the logged-in user's profile:
 * the allergens they selected, each with its hidden/scientific names, plus a
 * cross-contact warning. Meant to be handed to (or printed for) restaurant
 * staff. All client-side and driven by user.allergies — no network needed, so
 * it still works offline at a table.
 */

import React from 'react';
import { Printer, AlertTriangle, UtensilsCrossed } from 'lucide-react';
import useAuth from '../../hooks/useAuth';

// Mirrors the backend ALLERGEN_MAP (utils/allergenChecker.js). Kept local so the
// card always renders, even without connectivity. If you change one, change both.
const ALLERGEN_REFERENCE = {
  1: { name: 'Milk', hidden_names: ['casein', 'whey', 'lactose', 'cream', 'butter', 'cheese'] },
  2: { name: 'Eggs', hidden_names: ['albumin', 'globulin', 'lysozyme', 'mayonnaise', 'meringue'] },
  3: { name: 'Peanuts', hidden_names: ['arachis oil', 'groundnut'] },
  4: { name: 'Tree Nuts', hidden_names: ['almond', 'walnut', 'cashew', 'pistachio', 'pecan'] },
  5: { name: 'Shellfish', hidden_names: ['crustacean', 'shrimp', 'crab', 'lobster'] },
  6: { name: 'Fish', hidden_names: ['cod', 'salmon', 'tuna', 'anchovy'] },
  7: { name: 'Wheat', hidden_names: ['gluten', 'semolina', 'bulgur'] },
  8: { name: 'Soy', hidden_names: ['soybean', 'soya', 'shoyu'] },
  9: { name: 'Sesame', hidden_names: ['tahini', 'hummus', 'halvah'] },
  10: { name: 'Mustard', hidden_names: ['mustard seed', 'mustard powder'] },
};

export default function DiningOut() {
  const { user } = useAuth();
  const allergyIds = Array.isArray(user?.allergies) ? user.allergies : [];
  const allergens = allergyIds
    .map((id) => ALLERGEN_REFERENCE[id])
    .filter(Boolean);

  return (
    <div className="page-container">
      <h1 className="page-title">Dining Out</h1>
      <p className="dining-intro">
        Show or print this card for restaurant staff. It lists your allergens and
        the hidden names to watch for on labels and in the kitchen.
      </p>

      {allergens.length === 0 ? (
        <div className="card empty-state">
          <UtensilsCrossed size={48} className="empty-icon" />
          <p className="empty-text">
            You haven&apos;t selected any allergies yet, so there&apos;s nothing to
            put on a chef card. Create an account with your allergens selected to
            generate one.
          </p>
        </div>
      ) : (
        <>
          {/* The printable card. id is targeted by the print stylesheet. */}
          <div id="chef-card-print" className="chef-card">
            <div className="chef-card-header">
              <AlertTriangle size={28} aria-hidden="true" />
              <div>
                <p className="chef-card-eyebrow">FOOD ALLERGY ALERT</p>
                <h2 className="chef-card-title">
                  {user?.first_name ? `${user.first_name} has` : 'I have'} a severe
                  food allergy
                </h2>
              </div>
            </div>

            <p className="chef-card-lead">
              Please make sure my meal does <strong>not</strong> contain — and is
              not prepared with — any of the following:
            </p>

            <ul className="chef-allergen-list">
              {allergens.map((a) => (
                <li key={a.name} className="chef-allergen">
                  <span className="chef-allergen-name">{a.name}</span>
                  {a.hidden_names?.length > 0 && (
                    <span className="chef-allergen-hidden">
                      also: {a.hidden_names.join(', ')}
                    </span>
                  )}
                </li>
              ))}
            </ul>

            <div className="chef-card-warning">
              <strong>Even a trace can cause a serious reaction.</strong> Please use
              clean utensils, surfaces, and equipment to avoid cross-contact, and
              check with the kitchen if you&apos;re unsure. Thank you for keeping me
              safe.
            </div>

            <p className="chef-card-foot">Generated by SafePlate</p>
          </div>

          <button
            type="button"
            onClick={() => window.print()}
            className="btn btn-primary btn-full chef-print-btn"
          >
            <Printer size={18} /> Print / Save as PDF
          </button>
          <p className="chef-print-hint">
            Tip: in the print dialog, choose &ldquo;Save as PDF&rdquo; to keep it on
            your phone.
          </p>
        </>
      )}
    </div>
  );
}
